import type { ReactNode } from "react";

import { Wrench, Plane, User, Settings, AlertTriangle } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { fmt, formatDuration } from "./month/helper";

export interface ScheduleHoverCardData {
  jobOrderNo: string;
  processDesc: string;
  partNo: string;
  partDescription: string;
  customerName?: string | null;
  startDate: string;
  eddDate: string;
  duration: number;
  technicianName: string | null;
  machineName: string | null;
  /** Tailwind classes string like "aero:bg-green-500 aero:border-green-600" */
  priorityColor: string;
  /** Optional reason populated by backend when bar is unavailable (red). */
  unavailableReason?: string | null;
}

interface ScheduleHoverCardProps {
  data: ScheduleHoverCardData;
  /**
   * "job"         → process is primary title; job order + customer in airplane row
   * "technician"  → job order is primary title; process in airplane row
   * "machine"     → job order is primary title; process in airplane row
   */
  variant?: "job" | "technician" | "machine";
  /** Extra classes applied to the outermost wrapper — use to override rounding/shadow when stacking. */
  className?: string;
}

/** Maps combined priority class string → a single solid bg class for the accent bar. */
function getAccentClass(priorityColor: string): string {
  if (priorityColor.includes("green")) return "aero:bg-green-500";
  if (priorityColor.includes("yellow")) return "aero:bg-yellow-400";
  if (priorityColor.includes("red")) return "aero:bg-red-500";
  return "aero:bg-blue-500";
}

/** Maps combined priority class string → a text color class for title text. */
function getTextClass(priorityColor: string): string {
  if (priorityColor.includes("green")) return "aero:text-green-600";
  if (priorityColor.includes("yellow")) return "aero:text-yellow-600";
  if (priorityColor.includes("red")) return "aero:text-red-600";
  return "aero:text-blue-600";
}

export default function ScheduleHoverCard({
  data,
  variant = "job",
  className
}: ScheduleHoverCardProps) {
  const {
    jobOrderNo,
    processDesc,
    partNo,
    partDescription,
    customerName,
    startDate,
    eddDate,
    duration,
    technicianName,
    machineName,
    priorityColor,
    unavailableReason
  } = data;

  const accentClass = getAccentClass(priorityColor);
  const textClass = getTextClass(priorityColor);

  const durationLabel = formatDuration(duration);
  const timeRange = `${fmt(startDate)} – ${fmt(eddDate)}`;
  const timeLabel = [timeRange, durationLabel].filter(Boolean).join("  ");

  // processDesc already contains the full label, e.g. "2140 - Stress Relief Bake"
  const processLabel = processDesc || "—";

  // Primary bold title + secondary line below it (before divider)
  // job view:      title = process,                     secondary = job order no
  // tech/machine:  title = "JO-XXXX - process" (1 line), secondary = null
  const primaryTitle =
    variant === "job"
      ? processLabel
      : [jobOrderNo, processLabel].filter(Boolean).join(" - ");
  const secondaryTitle = variant === "job" ? jobOrderNo : null;

  // ✈️ Airplane row — customer/airline info only
  const customerLine = customerName || null;

  // 🔧 Wrench row — always part no + description ("PN-001 – Turbine Blade")
  const partLine = [partNo, partDescription].filter(Boolean).join(" – ") || "—";

  return (
    /*
     * Figma 197:118829 — 204×159px
     * Layout: 3px top accent bar → content area (p-2)
     *   • Time label (small muted): duration · time range
     *   • Bold title (up to 2 lines)
     *   • Divider
     *   • 4 icon rows: wrench / airplane / person / settings
     */
    <div
      className={cn(
        "aero:w-52 aero:overflow-hidden aero:rounded-md aero:shadow-lg aero:border aero:border-border",
        className
      )}
    >
      {/* 3px top accent bar — full width, priority color */}
      <div className={`aero:h-[3px] aero:w-full ${accentClass}`} />

      {/* Card body */}
      <div className="aero:bg-popover aero:text-popover-foreground aero:px-2 aero:pt-2 aero:pb-2">
        {/* ── Time + title section ── */}
        <div className="aero:mb-2">
          {/* Duration first, then time range */}
          <p className="aero:text-[9px] aero:text-neutral-900 aero:leading-none aero:mb-1">
            {timeLabel}
          </p>
          {/* Main title — bold, up to 2 lines */}
          <p
            className={`aero:text-[11px] aero:font-semibold aero:leading-snug aero:line-clamp-2 ${textClass}`}
          >
            {primaryTitle}
          </p>
          {/* Secondary line — job order (job view only) */}
          {secondaryTitle && (
            <p
              className={`aero:text-[10px] aero:leading-snug aero:mt-0.5 aero:truncate ${textClass} aero:opacity-80`}
            >
              {secondaryTitle}
            </p>
          )}
        </div>

        {/* Divider */}
        <div className="aero:border-t aero:border-border aero:mb-2" />

        {unavailableReason && (
          <div className="aero:mb-2 aero:flex aero:items-start aero:gap-1.5">
            <AlertTriangle className="aero:h-3 aero:w-3 aero:mt-[1px] aero:flex-shrink-0 aero:text-red-600" />
            <span className="aero:text-[10px] aero:leading-tight aero:break-words aero:text-red-600">
              Unavailable: {unavailableReason}
            </span>
          </div>
        )}

        {/* ── Icon rows ── */}
        <div className="aero:space-y-1">
          {/* 🔧 Wrench — Part No + Description */}
          <div className="aero:flex aero:items-start aero:gap-1.5">
            <Wrench className="aero:h-3 aero:w-3 aero:mt-[1px] aero:flex-shrink-0 aero:text-muted-foreground" />
            {partLine !== "—" ? (
              <span className="aero:text-[10px] aero:leading-tight aero:break-words aero:text-popover-foreground">
                {partLine}
              </span>
            ) : (
              <span className="aero:text-[10px] aero:leading-tight aero:break-words aero:italic aero:text-muted-foreground">
                No Part Number
              </span>
            )}
          </div>

          {/* ✈️ Airplane — Customer */}
          <div className="aero:flex aero:items-center aero:gap-1.5">
            <Plane className="aero:h-3 aero:w-3 aero:flex-shrink-0 aero:text-muted-foreground" />
            {customerLine ? (
              <span className="aero:text-[10px] aero:leading-tight aero:truncate aero:text-popover-foreground">
                {customerLine}
              </span>
            ) : (
              <span className="aero:text-[10px] aero:leading-tight aero:truncate aero:italic aero:text-muted-foreground">
                Unassigned Customer
              </span>
            )}
          </div>

          {/* 👤 Person — Technician */}
          <div className="aero:flex aero:items-center aero:gap-1.5">
            <User className="aero:h-3 aero:w-3 aero:flex-shrink-0 aero:text-muted-foreground" />
            {technicianName ? (
              <span className="aero:text-[10px] aero:leading-tight aero:truncate aero:text-popover-foreground">
                {technicianName}
              </span>
            ) : (
              <span className="aero:text-[10px] aero:leading-tight aero:truncate aero:italic aero:text-muted-foreground">
                Unassigned Technician
              </span>
            )}
          </div>

          {/* ⚙️ Settings — Machine */}
          <div className="aero:flex aero:items-center aero:gap-1.5">
            <Settings className="aero:h-3 aero:w-3 aero:flex-shrink-0 aero:text-muted-foreground" />
            {machineName ? (
              <span className="aero:text-[10px] aero:leading-tight aero:truncate aero:text-popover-foreground">
                {machineName}
              </span>
            ) : (
              <span className="aero:text-[10px] aero:leading-tight aero:truncate aero:italic aero:text-muted-foreground">
                Unassigned Machine
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── ScheduleBarTooltip ────────────────────────────────────────────────────────
/**
 * Reusable tooltip wrapper for schedule bar elements.
 *
 * Replaces the repeated:
 *   TooltipProvider > Tooltip > TooltipTrigger > bar div
 *                             > TooltipContent > ScheduleHoverCard
 *
 * Usage:
 * ```tsx
 * <ScheduleBarTooltip key={assignment.id} variant="technician" data={...}>
 *   <div className={barClasses} style={barStyle}>...</div>
 * </ScheduleBarTooltip>
 * ```
 */
export function ScheduleBarTooltip({
  variant = "job",
  data,
  children
}: {
  variant?: "job" | "technician" | "machine";
  data: ScheduleHoverCardData;
  children: ReactNode;
}) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent className="aero:p-0 aero:border-0 aero:bg-transparent aero:shadow-none">
          <ScheduleHoverCard variant={variant} data={data} />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
